// Bilingual content for the About page (src/components/About.tsx)

export interface AboutLanguageCard {
  flag: string;
  name: string;
  country: string;
  speakers: string;
  example: string;
}

export interface AboutFeatureCard {
  icon: string;
  title: string;
  desc: string;
}

export interface AboutStatCard {
  number: string;
  label: string;
}

export interface AboutContent {
  backLabel: string;
  pageTitle: string;
  whatIsTitle: string;
  whatIsP1: string;
  whatIsP2: string;
  languagesTitle: string;
  languages: AboutLanguageCard[];
  learnTitle: string;
  features: AboutFeatureCard[];
  statsTitle: string;
  stats: AboutStatCard[];
  contactTitle: string;
  contactText: string;
}

export const ABOUT_CONTENT: Record<'fr' | 'en', AboutContent> = {
  fr: {
    backLabel: 'Retour',
    pageTitle: 'À propos de Jogolinga',
    whatIsTitle: "Qu'est-ce que Jogolinga ?",
    whatIsP1: "Jogolinga est une application gratuite d'apprentissage des langues africaines. Elle permet d'apprendre le Wolof, le Bambara, le Lingala, le Peul, le Swahili et le Copte de manière interactive et progressive.",
    whatIsP2: "Chaque langue propose plus de 500 mots organisés en catégories thématiques : salutations, famille, nourriture, couleurs, corps humain, temps, verbes, objets du quotidien et nombres. Des leçons de grammaire, des exercices, des quiz culturels et des mini-jeux complètent l'apprentissage.",
    languagesTitle: 'Les langues disponibles',
    languages: [
      { flag: '🇸🇳', name: 'Wolof', country: 'Sénégal', speakers: '10 millions de locuteurs', example: 'Salamalekum — Bonjour' },
      { flag: '🇲🇱', name: 'Bambara', country: 'Mali', speakers: '15 millions de locuteurs', example: 'I ni ce — Bonjour' },
      { flag: '🇨🇩', name: 'Lingala', country: 'RD Congo', speakers: '70 millions de locuteurs', example: 'Mbote — Bonjour' },
      { flag: '🌍', name: 'Peul (Fula)', country: 'Guinée, Sahel', speakers: '40 millions de locuteurs', example: 'Jam waali — Bonjour' },
      { flag: '🌍', name: 'Swahili', country: 'Tanzanie, Kenya', speakers: '200 millions de locuteurs', example: 'Habari — Bonjour' },
      { flag: '🏛️', name: 'Copte', country: 'Égypte ancienne', speakers: 'Langue liturgique', example: 'Χερε — Bonjour' },
    ],
    learnTitle: 'Ce que vous pouvez apprendre',
    features: [
      { icon: '📚', title: 'Vocabulaire thématique', desc: 'Plus de 500 mots par langue en 10 catégories : salutations, famille, animaux, nourriture, couleurs, corps humain, temps, verbes, objets du quotidien et nombres.' },
      { icon: '🔤', title: 'Grammaire interactive', desc: 'Leçons structurées en 3 unités progressives couvrant les pronoms, les temps verbaux, la négation, les questions et les prépositions.' },
      { icon: '🎯', title: 'Révision intelligente', desc: 'Système de répétition espacée (SRS) qui planifie vos révisions selon vos résultats pour maximiser la mémorisation à long terme.' },
      { icon: '🎮', title: 'Mini-jeux culturels', desc: "Quiz sur l'histoire, la géographie et la culture africaine pour enrichir votre apprentissage au-delà des mots." },
    ],
    statsTitle: 'Jogolinga en chiffres',
    stats: [
      { number: '6', label: 'Langues africaines' },
      { number: '500+', label: 'Mots par langue' },
      { number: '10', label: 'Catégories thématiques' },
      { number: '3', label: 'Unités de grammaire' },
    ],
    contactTitle: 'Contact',
    contactText: 'Pour toute question ou suggestion, vous pouvez nous contacter à l\'adresse :',
  },
  en: {
    backLabel: 'Back',
    pageTitle: 'About Jogolinga',
    whatIsTitle: 'What is Jogolinga?',
    whatIsP1: 'Jogolinga is a free app for learning African languages. It lets you learn Wolof, Bambara, Lingala, Fula, Swahili, Yoruba, and Coptic in an interactive, progressive way.',
    whatIsP2: 'Each language offers over 500 words organized into thematic categories: greetings, family, food, colors, the human body, time, verbs, everyday objects, and numbers. Grammar lessons, exercises, cultural quizzes, and mini-games round out the learning experience.',
    languagesTitle: 'Available languages',
    languages: [
      { flag: '🇸🇳', name: 'Wolof', country: 'Senegal', speakers: '10 million speakers', example: 'Salamalekum — Hello' },
      { flag: '🇲🇱', name: 'Bambara', country: 'Mali', speakers: '15 million speakers', example: 'I ni ce — Hello' },
      { flag: '🇨🇩', name: 'Lingala', country: 'DR Congo', speakers: '70 million speakers', example: 'Mbote — Hello' },
      { flag: '🌍', name: 'Fula (Peul)', country: 'Guinea, Sahel', speakers: '40 million speakers', example: 'Jam waali — Hello' },
      { flag: '🌍', name: 'Swahili', country: 'Tanzania, Kenya', speakers: '200 million speakers', example: 'Habari — Hello' },
      { flag: '🏛️', name: 'Coptic', country: 'Ancient Egypt', speakers: 'Liturgical language', example: 'Χερε — Hello' },
    ],
    learnTitle: 'What you can learn',
    features: [
      { icon: '📚', title: 'Thematic vocabulary', desc: 'Over 500 words per language across 10 categories: greetings, family, animals, food, colors, the human body, time, verbs, everyday objects, and numbers.' },
      { icon: '🔤', title: 'Interactive grammar', desc: 'Lessons structured in 3 progressive units covering pronouns, verb tenses, negation, questions, and prepositions.' },
      { icon: '🎯', title: 'Smart review', desc: 'A spaced repetition system (SRS) that schedules your reviews based on your results to maximize long-term retention.' },
      { icon: '🎮', title: 'Cultural mini-games', desc: 'Quizzes on African history, geography, and culture to enrich your learning beyond the words themselves.' },
    ],
    statsTitle: 'Jogolinga by the numbers',
    stats: [
      { number: '6', label: 'African languages' },
      { number: '500+', label: 'Words per language' },
      { number: '10', label: 'Thematic categories' },
      { number: '3', label: 'Grammar units' },
    ],
    contactTitle: 'Contact',
    contactText: 'For any question or suggestion, you can reach us at:',
  },
};
